import React, { useEffect } from 'react';
import { pick, prop } from 'ramda';
import { useOAPISocket } from 'server';
import { Helmet } from 'react-helmet-async';
import { useGetTags } from 'js/hooks/useGetTags';
import { usePas, useAutoLogin } from 'js/hooks/auth';
import { usePublicApi } from 'js/hooks/usePublicAPI';
import { TagManager } from 'js/components/system/TagManager';
import { ErrorMessage } from 'js/components/common/ErrorMessage';
import { PopupManager } from 'js/components/common/PopupManager';
import { useAppSelector, useAuthSelector, useDeviceSelector, usePageSelector, useUserSelector } from 'js/store/hooks';
import { publicEventsAPIService } from 'js/services/publicEventsAPI.service';
import { usePromisedReady } from 'js/hooks/usePromisedReady';
import {
    useClientIP,
    useDetectLanguage,
    useDetectUserDetails,
    useGeoIP,
    useDetectCurrency,
    useAdminRole,
    useUserKeepAlive
} from 'js/hooks/user';

import * as hooks from './hooks';
import { AppPostInit } from './components/AppPostInit';
import { AppContext } from './store/context';

import style from './App.scss';

export const App = ({ children }) => {
    const htmlBaseClasses = useDeviceSelector(prop('htmlBaseClasses'));
    const isPageRendering = usePageSelector(prop('isPageRendering'));
    const togglePreloader = hooks.useTogglePreloader();
    const fullLang = useUserSelector(prop('lang'))?.replace('_', '-');
    const [{ isLoading: isAppLoading, app: appData, error }] = hooks.useAppInitialResources();
    const [{ isLoading: isTagsLoading }] = useGetTags();
    const skipAutoLoginDependency = hooks.useSkipAutoLoginDependency();
    const { isSportsbookRootURLReady, isLottoRootURLReady } = useAppSelector(pick(['isSportsbookRootURLReady', 'isLottoRootURLReady']));
    const wasAutoLoginCompleted = useAuthSelector(prop('wasAutoLoginCompleted'));
    const isAppReady = !isAppLoading
        && isSportsbookRootURLReady
        && isLottoRootURLReady
        && (wasAutoLoginCompleted || skipAutoLoginDependency);
    const isTagsReady = appData && !isTagsLoading && (wasAutoLoginCompleted || skipAutoLoginDependency);
    const {
        isErrorVisible,
        errorText
    } = useAppSelector(pick(['isErrorVisible', 'errorText']));

    usePas();

    useOAPISocket();
    useGeoIP();
    useClientIP();
    useUserKeepAlive();
    hooks.usePushNotifier();

    useAutoLogin();

    useAdminRole();
    usePublicApi();
    hooks.useHotKeys();
    useDetectLanguage();
    useDetectCurrency();
    useDetectUserDetails();
    hooks.useAppNotifier();
    hooks.useEnableLogger();
    hooks.useDraftThemeCookie();
    hooks.useGetMenuItems();
    hooks.useRefreshSafari();
    hooks.useSportsbookIntegration();
    hooks.useLottoIntegration();
    hooks.useGamesInitialResources();
    hooks.useOnDeviceChange();

    const [isFullyReady, addLoadingPromise] = usePromisedReady({
        isReadyToCapture: isAppReady && isTagsReady && !isPageRendering
    });

    useEffect(() => {
        const isReadyToShow = Boolean(isAppReady && isTagsReady && isFullyReady);

        togglePreloader(isReadyToShow);

        if (isReadyToShow) {
            publicEventsAPIService.trigger('app-ready');
        }
    }, [isAppReady, isTagsReady, isFullyReady, togglePreloader]);

    if (error) {
        const errorMessage = typeof error === 'string' || error instanceof window.Error
            ? error.toString()
            : 'playtech.mobile.system.error.no.response';

        return <ErrorMessage className={style.error} message={errorMessage} />;
    }

    if (isErrorVisible) {
        return <ErrorMessage className={style.error} message={errorText} />;
    }

    return (
        <>
            {isAppReady && (
                <>
                    <Helmet>
                        <html lang={fullLang} className={htmlBaseClasses} />
                    </Helmet>
                    <AppPostInit />
                    <AppContext.Provider value={{ addLoadingPromise }}>
                        {children}
                    </AppContext.Provider>
                    <PopupManager />
                </>
            )}
            {isTagsReady && <TagManager />}
        </>
    );
};
